'use client'

import { Emoji } from '@/components/between-sessions/Emoji'

// The FIRST of the two flagship differentiator sections. Everything in
// Between Sessions that isn't community/tools rests on this: your dyad
// supervisor knows you across a cohort, AND you have a team of
// specialists to pull expertise from when a case needs it. Traditional
// supervision = one person, done. This section makes the difference
// explicit so cold visitors don't confuse the offering with "just
// another supervision service."

// Radial visual — YOU at the center, 6 supervisors arranged in a
// hexagonal ring around you. One is marked as "your primary" (star
// badge + filled bg). Each shows their specialization so the reader
// sees the range of expertise at one glance. Modalities are the 6
// most-recognized: general case consultation, EMDR, IFS, complex
// trauma, couples, DBT. Cherry can swap in the actual team profile
// once she has it.
const RING_SUPERVISORS: {
  modality: string
  icon: string
  angleDeg: number
  isPrimary?: boolean
}[] = [
  { modality: 'General', icon: 'fluent-emoji-high-contrast:speech-balloon', angleDeg: 0 },
  { modality: 'EMDR', icon: 'fluent-emoji-high-contrast:brain', angleDeg: 60 },
  { modality: 'IFS', icon: 'fluent-emoji-high-contrast:seedling', angleDeg: 120, isPrimary: true },
  { modality: 'Couples', icon: 'fluent-emoji-high-contrast:link', angleDeg: 180 },
  { modality: 'DBT', icon: 'fluent-emoji-high-contrast:sparkles', angleDeg: 240 },
  { modality: 'Trauma', icon: 'fluent-emoji-high-contrast:heart-hands', angleDeg: 300 },
]

// Convert a clock-angle (0deg = 12 o'clock, clockwise) into
// {top,left} percentages inside a square container. Radius = 40%
// so labels stay inside the container edge.
function angleToPos(angleDeg: number, radius = 40) {
  const rad = ((angleDeg - 90) * Math.PI) / 180
  const left = 50 + radius * Math.cos(rad)
  const top = 50 + radius * Math.sin(rad)
  return { top: `${top}%`, left: `${left}%` }
}

function SupervisionRing() {
  return (
    <div className="relative w-full aspect-square max-w-[380px] mx-auto">
      {/* Center: YOU */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10 flex flex-col items-center gap-1.5">
        <div className="w-20 h-20 rounded-full bg-gray-900 shadow-[0_14px_36px_-12px_rgba(20,20,20,0.5)] flex items-center justify-center text-white">
          <Emoji name="fluent-emoji-high-contrast:person" size={40} />
        </div>
        <span className="text-[11px] font-display font-bold text-gray-900 tracking-tight uppercase">
          You
        </span>
      </div>

      {/* Faint dashed ring outlining the "team around you" — subtle,
          reinforces the circle metaphor without competing with the
          circles themselves. */}
      <div
        aria-hidden
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border border-dashed border-bs-secondary-300"
        style={{ width: '80%', height: '80%' }}
      />

      {/* Supervisor circles arranged around You */}
      {RING_SUPERVISORS.map(sup => {
        const pos = angleToPos(sup.angleDeg)
        const isPrimary = !!sup.isPrimary
        return (
          <div
            key={sup.modality}
            className="absolute -translate-x-1/2 -translate-y-1/2 flex flex-col items-center gap-1"
            style={pos}
          >
            <div
              className={
                'relative rounded-full flex items-center justify-center transition-all ' +
                (isPrimary
                  ? 'w-14 h-14 bg-bs-secondary-500 border-2 border-bs-secondary-600 text-white shadow-[0_12px_28px_-10px_rgba(108,95,199,0.55)]'
                  : 'w-12 h-12 bg-white border border-[#ececec] text-bs-secondary-500 shadow-[0_6px_16px_-8px_rgba(20,20,20,0.15)]')
              }
            >
              <Emoji name={sup.icon} size={isPrimary ? 26 : 22} />
              {isPrimary && (
                <span
                  aria-label="Your primary supervisor"
                  className="absolute -top-1.5 -right-1.5 inline-flex items-center justify-center w-5 h-5 rounded-full bg-white text-bs-secondary-600 text-[11px] font-display font-bold shadow-[0_4px_10px_-3px_rgba(20,20,20,0.3)]"
                >
                  ★
                </span>
              )}
            </div>
            <span
              className={
                'text-[10px] font-display font-semibold tracking-tight whitespace-nowrap ' +
                (isPrimary ? 'text-gray-900' : 'text-gray-700')
              }
            >
              {sup.modality}
            </span>
          </div>
        )
      })}
    </div>
  )
}

// Each format card now carries the tour's full detail — evocative
// subline in a lavender pillow, a numbers strip for the concrete
// duration/scale, and 3 bulleted points with a bolded lead. Cards
// grow taller but still fit as a 3-column grid on desktop and stack
// cleanly on mobile. Tier-specific inclusion callouts are kept
// EXPLICIT (e.g. "Complete or Complete+ only") — that's not
// misleading, it's helpful for readers deciding on a tier.
// Each format also carries a `tierBadge` — the tiers it's INCLUDED
// in. Rendered as a small pill on the card so we don't have to
// repeat "(Complete or Complete+ only)" in every bullet. Individual
// has no badge because it's available as an add-on for every tier.
const FORMATS: {
  emoji: string
  title: string
  tierBadge?: string
  numbers: { value: string; unit: string; subBody?: string }[]
  bullets: { lead: string; body: string }[]
}[] = [
  {
    emoji: 'fluent-emoji-high-contrast:link',
    title: 'Dyad supervision',
    tierBadge: 'Included in Complete & Complete+',
    numbers: [
      {
        value: '90min',
        unit: 'direct supervision hour',
        subBody: '60 live + 30 min reflection after.',
      },
    ],
    bullets: [
      {
        lead: 'A partner who grows with you',
        body: 'same peer for the whole 4-month cohort.',
      },
      {
        lead: 'Your person for the cohort',
        body: 'pick from a deeply specialized team, or continue with your Alli practicum supervisor.',
      },
      {
        lead: 'Listed on client receipts',
        body: 'for clients who need supervisor sign-off for insurance.',
      },
    ],
  },
  {
    emoji: 'fluent-emoji-high-contrast:busts-in-silhouette',
    title: 'Group supervision',
    tierBadge: 'Included in Community & up',
    numbers: [
      {
        value: '90min',
        unit: 'live session',
        subBody: 'Up to 8 therapists per group.',
      },
    ],
    bullets: [
      {
        lead: 'Many groups every month',
        body: 'different supervisors and modalities — case consultation, EMDR, IFS, couples, and more.',
      },
      {
        lead: 'Pick whatever resonates',
        body: 'the group, the modality, or the supervisor you want to learn from.',
      },
      {
        lead: '1 group/month included',
        body: 'plus the option to add more anytime. Core members can add on any group too.',
      },
    ],
  },
  {
    emoji: 'fluent-emoji-high-contrast:speech-balloon',
    title: 'Individual supervision',
    tierBadge: 'Add-on · every tier',
    numbers: [
      {
        value: '90min',
        unit: 'direct supervision hour',
        subBody: '60 live + 30 min reflection after.',
      },
    ],
    bullets: [
      {
        lead: 'Undivided attention',
        body: 'just you and the supervisor on your case.',
      },
      {
        lead: 'Book only when a case needs it',
        body: 'no commitment. For the cases that are complex, ethically loaded, or stuck.',
      },
      {
        lead: 'Choose the supervisor for the case',
        body: 'pick whoever fits what you’re working through.',
      },
    ],
  },
]

export function LandingSupervisionDeepDive() {
  return (
    <section className="px-6 py-24 sm:py-32 bg-white border-y border-[#ececec]">
      <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,0.9fr)] gap-16 lg:gap-20 items-center">
        {/* Left: opinion + points */}
        <div>
          <p className="text-[11px] uppercase tracking-[0.22em] text-bs-secondary-700 font-display font-semibold mb-4">
            Supervision
          </p>
          <h2 className="font-display font-bold text-4xl sm:text-5xl leading-[1.05] tracking-tight text-gray-900 mb-6">
            <span className="block">One supervisor can&rsquo;t know every kind of case.</span>
            <span className="block mt-2">
              <span className="relative inline-block italic text-bs-secondary-500">
                A whole team
                <svg
                  aria-hidden
                  viewBox="0 0 300 22"
                  preserveAspectRatio="none"
                  className="absolute left-0 -bottom-2 w-full h-3 text-bs-secondary-500 overflow-visible"
                >
                  <path
                    d="M 4 12 C 50 5, 110 17, 165 10 S 245 6, 296 15"
                    stroke="currentColor"
                    strokeWidth="10"
                    strokeLinecap="round"
                    fill="none"
                  />
                </svg>
              </span>{' '}
              can.
            </span>
          </h2>
          {/* Bullets — verbatim from the tour's SupervisionSlideView
              opener. This is the exact language Cherry wants leading
              this section: the 4 points that make BS supervision
              different from typical solo-supervisor arrangements. No
              intermediate copy paragraph — the H2 above sets it up
              and the bullets carry the whole argument. */}
          <ul className="space-y-4">
            {[
              {
                lead: 'A whole team, not just one supervisor',
                body: 'outside, most therapists work with one. Here, a room of specialists to pull from, each with their own expertise. More affordable, endlessly more expansive.',
              },
              {
                lead: 'Your person, for the cohort',
                body: 'on Complete and Complete+, your dyad supervisor stays with you the full 4 months. Someone who knows your work session over session, that you can call on.',
              },
              {
                lead: 'You’re the one who decides',
                body: 'one supervisor across every format, or a different one for each. Who, how many, when — it’s all your call.',
              },
              {
                lead: 'Three formats to mix and match',
                body: 'dyad, group, and 1:1.',
              },
            ].map(p => (
              <li key={p.lead} className="flex items-start gap-3">
                <span className="flex-shrink-0 mt-2 w-1.5 h-1.5 rounded-full bg-bs-secondary-500" />
                <span className="text-[15px] text-gray-700 leading-relaxed">
                  <span className="font-display font-semibold text-gray-900">
                    {p.lead}
                  </span>{' '}
                  — {p.body}
                </span>
              </li>
            ))}
          </ul>
        </div>

        {/* Right: radial visual — YOU at the center, 6 supervisors in
            a hexagonal ring around you. One is marked as "your
            primary" (star badge + filled lavender bg) — the others
            are available through group + 1:1 formats. Each shows
            their specialization so the reader sees the range of
            expertise on the team at one glance.

            Positions calculated as 60deg increments starting at 12
            o'clock. Radius = 40% of container so labels stay inside.
            Container is aspect-square so the ring stays circular
            regardless of section width. */}
        <div className="relative">
          <div className="relative rounded-[2rem] border border-[#ececec] bg-bs-secondary-50 p-6 sm:p-8 shadow-[0_30px_60px_-30px_rgba(108,95,199,0.35)]">
            <p className="text-[10px] uppercase tracking-[0.22em] text-bs-secondary-700 font-display font-semibold mb-2 text-center">
              Your supervision team
            </p>
            <p className="text-[13px] text-gray-600 font-display font-medium mb-6 text-center">
              Every specialty, one team.
            </p>

            <SupervisionRing />

            <p className="mt-6 text-center text-[11px] text-gray-600 font-display font-medium leading-relaxed">
              <span className="text-bs-secondary-600 font-bold">★</span>{' '}
              Your primary stays with you across the cohort. The rest are
              available through group + 1:1 formats — whenever a case
              needs their expertise.
            </p>
          </div>
        </div>
      </div>

      {/* Format strip — 3 rich cards, one per format. Each carries
          the full detail Cherry pulled from the tour: emoji header +
          title + numbers strip + 3 bulleted points with bolded leads.
          Cards grow taller than the compact strip we started with,
          but the depth is what makes the section land — a reader
          skimming the bullets alone gets the full picture of each
          format. */}
      <div className="max-w-6xl mx-auto mt-20 pt-16 border-t border-[#ececec]">
        {/* Action-oriented header — the format cards below now read
            as a choice the reader is about to make, not a taxonomy
            they're being taught. Combines what used to be an eyebrow
            + subhead into one clear line. */}
        <div className="text-center mb-10 max-w-xl mx-auto">
          <h3 className="font-display font-bold text-2xl sm:text-3xl leading-tight text-gray-900">
            Pick your supervision format.
          </h3>
          <p className="mt-2 text-[15px] italic text-gray-500 font-display">
            Or use all three.
          </p>
        </div>

        {/* Tier intro callout — 4 tier mini-cards showing WHAT varies
            per tier (the supervision spec). Concrete instead of
            abstract: reader sees at a glance that Core = à la carte,
            Complete+ = 4 dyads + 1 group, etc. Also serves as visual
            memory for the tier badges on the format cards below —
            reader recognizes "Complete & Complete+" because they just
            saw those tier names as cards. */}
        <div className="max-w-4xl mx-auto mb-12 rounded-[1.4rem] border border-bs-secondary-200 bg-bs-secondary-50 p-6 sm:p-8 shadow-[0_20px_44px_-22px_rgba(108,95,199,0.3)]">
          <p className="text-center text-[10px] uppercase tracking-[0.22em] text-bs-secondary-700 font-display font-semibold mb-3">
            Membership tiers
          </p>
          <h4 className="text-center font-display font-bold text-xl sm:text-2xl leading-tight text-gray-900 mb-2">
            <span className="italic text-bs-secondary-500">4 tiers</span>. One program.
          </h4>
          <p className="text-center text-[13px] text-gray-600 max-w-md mx-auto mb-7">
            Only supervision varies between them. Everything else — every
            member gets.
          </p>

          {/* 4 tier mini-cards — name + supervision spec per tier */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { name: 'Core', supervision: 'À la carte supervision', accent: '' },
              { name: 'Community', supervision: '1 group / mo', accent: '' },
              {
                name: 'Complete',
                supervision: '2 dyads + 1 group / mo',
                accent: '',
              },
              {
                name: 'Complete+',
                supervision: '4 dyads + 1 group / mo',
                accent: '',
              },
            ].map(t => (
              <div
                key={t.name}
                className="rounded-2xl bg-white border border-bs-secondary-200 p-4 sm:p-5 text-center shadow-[0_6px_16px_-8px_rgba(20,20,20,0.12)] hover:-translate-y-0.5 hover:shadow-[0_10px_22px_-10px_rgba(108,95,199,0.35)] hover:border-bs-secondary-300 transition-all duration-200"
              >
                <p className="text-[14px] sm:text-[15px] font-display font-bold text-gray-900 mb-2">
                  {t.name}
                </p>
                <div className="mx-auto w-6 h-px bg-bs-secondary-300 mb-2.5" />
                <p className="text-[11.5px] text-bs-secondary-700 font-display font-semibold leading-snug">
                  {t.supervision}
                </p>
              </div>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {FORMATS.map(f => (
            <div
              key={f.title}
              className="relative rounded-[1.4rem] border border-[#ececec] bg-white p-6 sm:p-7 flex flex-col hover:-translate-y-1 hover:shadow-[0_16px_32px_-16px_rgba(20,20,20,0.18)] hover:border-bs-secondary-200 transition-all duration-300"
            >
              {/* Emoji header + tier badge */}
              <div className="flex items-start justify-between gap-3 mb-4">
                <div className="w-12 h-12 rounded-full bg-bs-secondary-50 border border-bs-secondary-100 flex items-center justify-center text-bs-secondary-500">
                  <Emoji name={f.emoji} size={26} />
                </div>
                {f.tierBadge && (
                  <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-bs-secondary-50 border border-bs-secondary-100 text-bs-secondary-700 text-[10px] font-display font-semibold uppercase tracking-[0.12em] whitespace-nowrap">
                    {f.tierBadge}
                  </span>
                )}
              </div>

              {/* Title */}
              <h4 className="font-display font-bold text-xl sm:text-2xl leading-tight text-gray-900 mb-5">
                {f.title}.
              </h4>

              {/* Numbers strip — 1 or 2 stat blocks */}
              <div
                className={
                  'grid gap-2 mb-6 ' +
                  (f.numbers.length === 1 ? 'grid-cols-1' : 'grid-cols-2')
                }
              >
                {f.numbers.map((n, i) => (
                  <div
                    key={i}
                    className="rounded-xl bg-[#faf9f6] border border-[#ececec] p-3 text-center"
                  >
                    <div className="font-display font-bold text-2xl text-gray-900 tabular-nums leading-none">
                      {n.value}
                    </div>
                    <div className="text-[11px] font-display font-semibold text-bs-secondary-600 mt-2 leading-tight">
                      {n.unit}
                    </div>
                    {n.subBody && (
                      <div className="text-[10.5px] text-gray-500 mt-1.5 leading-snug">
                        {n.subBody}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Bullets — bold lead + body, tour style */}
              <ul className="space-y-3 mt-auto">
                {f.bullets.map(b => (
                  <li key={b.lead} className="flex items-start gap-2.5">
                    <span className="flex-shrink-0 mt-2 w-1 h-1 rounded-full bg-bs-secondary-500" />
                    <span className="text-[12.5px] text-gray-700 leading-relaxed">
                      <span className="font-display font-semibold text-gray-900">
                        {b.lead}
                      </span>{' '}
                      — {b.body}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Clarifying footer — the tier badges above show what's
            INCLUDED per tier, but every tier can also book any format
            as an à la carte add-on. This one line avoids the "dyad is
            Complete-only" misread without cluttering each card. */}
        <p className="mt-6 text-center text-[12.5px] text-gray-500 leading-relaxed max-w-md mx-auto">
          Every tier can add supervision à la carte — the badges show
          what&rsquo;s already included in your membership.
        </p>
      </div>
    </section>
  )
}
